import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DateAdapter } from '@angular/material/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs';
import { baseLanguages } from '../../core/header/lang-selector/lang-selector.component';
import {
  City,
  Country,
  CountryService,
} from '../../core/services/country-service';
import { DialogService } from '../../core/services/dialog-service';
import { OwnerRegisterModel } from '../../core/services/models/user';
import { jumpToErrorNode } from '../../core/services/utility';
import { BaseComponent } from '../../shared/component-base';
import { CountryPickerComponent } from '../../shared/components/country-picker/country-picker.component';
import { DogBreedPickerComponent } from '../../shared/components/dog-breed-picker/dog-breed-picker.component';
import { GloboDogValidators } from '../../shared/globo-dog-validators';

@Component({
  selector: 'app-register-for-owner',
  templateUrl: './register-for-owner.component.html',
  styleUrl: './register-for-owner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterForOwnerComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private readonly adapter = inject<DateAdapter<unknown, unknown>>(DateAdapter);
  private readonly translate = inject(TranslateService);
  private readonly countryService = inject(CountryService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);

  readonly fb = inject(FormBuilder);
  registerForm = this.fb.nonNullable.group({
    avatar: [new Blob(), [GloboDogValidators.requiredBlobFile()]],
    values: this.fb.group({
      language: ['hu', []],
      email: ['', [Validators.email, Validators.required]],
      email2: ['', [Validators.email, Validators.required]],
      password: [
        '',
        [
          Validators.required,
          GloboDogValidators.passwordLength(),
          GloboDogValidators.passwordHasNumber(),
          GloboDogValidators.passwordHasLowercaseUppercase(),
        ],
      ],
      password2: ['', [Validators.required]],
      avatar_name: ['', [Validators.required]],
      country: ['', [Validators.required]],
      city: ['', [Validators.required]],
      name: [''],
      newsletter: [false],
    }),
    dog_avatar: [new Blob(), [GloboDogValidators.requiredBlobFile()]],
    dog_values: this.fb.nonNullable.group({
      name: this.fb.nonNullable.control<string | undefined>(
        undefined,
        Validators.required
      ),
      registered_name: [''],
      breed_id: this.fb.nonNullable.control<number | null>(
        null,
        Validators.required
      ),
      gender_neuter: this.fb.nonNullable.control<number | undefined>(
        undefined,
        [Validators.required]
      ),
      female: this.fb.nonNullable.control<boolean | undefined>(
        undefined,
        Validators.required
      ),
      neutered: this.fb.nonNullable.control<boolean | undefined>(
        undefined,
        Validators.required
      ),
      birthday: this.fb.nonNullable.control<Date | undefined>(undefined),
      mixed: [false, Validators.required],
    }),
    termsOfService: [false, [Validators.requiredTrue]],
  });

  @ViewChild('countryInput') countryInput:
    | ElementRef<HTMLInputElement>
    | undefined;

  private countries: Country[] = [];
  private cities: City[] = [];
  filteredCities = signal<City[]>([]);
  filteredCountries = signal<Country[]>([]);

  private onLocalLangChangeSubscription;
  public lang: string;

  futureFilter = (d: Date | null): boolean => {
    return d! < new Date();
  };

  constructor(private ref: ChangeDetectorRef) {
    super();
    this.lang = this.translate.currentLang;
    this.adapter.setLocale(this.translate.currentLang);
    this.onLocalLangChangeSubscription = this.translate.onLangChange.subscribe(
      (langEvent: LangChangeEvent) => {
        this.adapter.setLocale(langEvent.lang);
        this.lang = langEvent.lang;
        this.updateTermsOfServiceTranslateParams();
        this.ref.detectChanges();
      }
    );

    this.countryService.getCountries().subscribe(countryList => {
      this.countries = countryList;
    });
    this.countryService.getCities().subscribe(cityList => {
      this.cities = cityList;
    });

    this.registerForm.controls.values.controls.city.valueChanges
      .pipe(
        startWith(''),
        filter(val => {
          return val != undefined && val.length >= 2;
        }),
        map(val => {
          const filterString = val || '';
          return filterString;
        })
      )
      .subscribe(filterString => {
        this.filteredCities.set(this.cityFilter(filterString));
      });
    this.restApiService.getFiles().subscribe({
      next: files => {
        files.forEach(f => {
          const key = f.key.slice(0, -3);
          if (key in this.staticFiles) {
            const lang = f.key.split('_').pop();
            if (lang) {
              this.staticFiles[key].files[lang] = f.file?.id || null;
            } else {
              console.error('bad lang for this key', f);
            }
          } else {
            console.error('key in not in the list', key, this.staticFiles);
          }
        });

        this.updateTermsOfServiceTranslateParams();
        this.ref.detectChanges();
      },
    });

    this.registerForm.controls.dog_values.controls.gender_neuter.valueChanges.subscribe(
      val => {
        switch (val) {
          case 0: // ivaros kan
            this.registerForm.controls.dog_values.controls.female.setValue(
              false
            );
            this.registerForm.controls.dog_values.controls.neutered.setValue(
              false
            );
            break;
          case 1: // ivartalan kan
            this.registerForm.controls.dog_values.controls.female.setValue(
              false
            );
            this.registerForm.controls.dog_values.controls.neutered.setValue(
              true
            );
            break;
          case 2: // ivaros szuka
            this.registerForm.controls.dog_values.controls.female.setValue(
              true
            );
            this.registerForm.controls.dog_values.controls.neutered.setValue(
              false
            );
            break;
          case 3: // ivartalan szuka
            this.registerForm.controls.dog_values.controls.female.setValue(
              true
            );
            this.registerForm.controls.dog_values.controls.neutered.setValue(
              true
            );
            break;
        }
      }
    );
  }

  private updateTermsOfServiceTranslateParams() {
    this.termsOfServiceTranslateParams = {
      url_tos_start:
        '<a class="primary" href="' +
        this.restApiService.FILE_URL +
        this.staticFiles['tos'].files[this.lang] +
        '" target="_blank">',
      url_tos_end: '</a>',
      url_aszf_start:
        '<a class="primary" href="' +
        this.restApiService.FILE_URL +
        this.staticFiles['aszf'].files[this.lang] +
        '" target="_blank">',
      url_aszf_end: '</a>',
      url_gdpr_start:
        '<a class="primary" href="' +
        this.restApiService.FILE_URL +
        this.staticFiles['gdpr'].files[this.lang] +
        '" target="_blank">',
      url_gdpr_end: '</a>',
    };
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.onLocalLangChangeSubscription.unsubscribe();
  }

  filterCountries(): void {
    const val = this.countryInput?.nativeElement.value || '';
    const countries = this.countryFilter(val);
    this.filteredCountries.set(countries);
  }

  private cityFilter(filterString: string): City[] {
    const filteredList = this.cities.filter(c =>
      c.name?.toLocaleLowerCase().includes(filterString.toLocaleLowerCase())
    );
    return filteredList;
  }

  private countryFilter(filterString: string): Country[] {
    const filteredList = this.countries.filter(c =>
      c.name?.toLocaleLowerCase().includes(filterString.toLocaleLowerCase())
    );
    return filteredList;
  }

  public override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.registerForm.controls.values.controls.email2.addValidators(
      GloboDogValidators.controlEquals(
        this.registerForm.controls.values.controls.email
      )
    );
    this.registerForm.controls.values.controls.password2.addValidators(
      GloboDogValidators.controlEquals(
        this.registerForm.controls.values.controls.password
      )
    );
  }

  public changeMixedCheckbox = (event: MatCheckboxChange) => {
    if (event.checked) {
      this.registerForm.controls.dog_values.controls.breed_id.disable();
    } else {
      this.registerForm.controls.dog_values.controls.breed_id.enable();
    }
  };

  protected updateImage(imageBlob: Blob, control: AbstractControl): void {
    control.patchValue(imageBlob);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  @ViewChild(CountryPickerComponent) countryPicker?: CountryPickerComponent;
  @ViewChild(DogBreedPickerComponent) dogBreedPicker?: DogBreedPickerComponent;
  public register(): void {
    this.registerForm.controls.values.controls.email2.disable();
    this.registerForm.controls.values.controls.password2.disable();
    this.registerForm.markAllAsTouched();
    this.countryPicker?.detectChanges();
    this.dogBreedPicker?.detectChanges();

    const lang = this.translate.currentLang;
    if (baseLanguages.includes(lang)) {
      this.registerForm.controls.values.controls.language.setValue(lang);
    } else {
      this.registerForm.controls.values.controls.language.setValue('en');
    }

    const registerModel: OwnerRegisterModel = this.registerForm.value;
    if (registerModel.dog_values?.mixed) {
      registerModel.dog_values.breed_id = null;
    }

    //console.log(registerModel);

    if (this.registerForm.invalid) {
      this.registerForm.controls.values.controls.email2.enable();
      this.registerForm.controls.values.controls.password2.enable();
      this.dialogService
        .openAlertDialog(_('Hiba'), _('Hibás, vagy hiányos adatok.'))
        .subscribe(() => {
          jumpToErrorNode();
        });
      return;
    }

    this.registerPending = true;

    this.restApiService.registerOwner(registerModel).subscribe({
      complete: () => {
        console.log('Register complete');
      },
      next: resp => {
        console.log('Register success ', resp);
        this.router.navigate([
          '/',
          this.translate.currentLang,
          'register-success',
          { email: this.registerForm.value.values!.email },
        ]);
        this.registerPending = false;
      },
      error: err => {
        console.error('Register error: ', err);
        this.registerForm.controls.values.controls.email2.enable();
        this.registerForm.controls.values.controls.password2.enable();

        if (err?.error?.detail == 'Email already registered') {
          this.dialogService.openAlertDialog(
            _('Hiba'),
            _(
              'E-mail cím foglalt! Egy e-mail címmel csak egy profil regisztrálható!'
            )
          );
        } else {
          this.dialogService.openAlertDialog(
            _('Hiba'),
            _('Hiba történt regisztráció közben.') + " " + _("Szerver válasza:") + err?.error?.detail
          );
        }

        this.registerPending = false;
      },
    });
  }

  public registerPending = false;

  public staticFiles: {
    [key in string]: {
      name: string;
      files: { [lang in string]: number | null };
    };
  } = {
    aszf: {
      name: 'ÁSZF',
      files: {},
    },
    aviation: {
      name: 'Repülőtársaságok',
      files: {},
    },
    gdpr: {
      name: 'GDPR',
      files: {},
    },
    tos: {
      name: 'Oldalhasználati feltételek',
      files: {},
    },
    travel_rules: {
      name: "Beutazási szabályok'",
      files: {},
    },
  };

  public termsOfServiceTranslateParams: any;
}
